(function () {
  'use strict';

  function toNumber(value) {
    var number = Number(value);
    return Number.isFinite(number) ? number : 0;
  }

  function tokens(value) {
    return String(value || '').split(/\s+/).filter(Boolean);
  }

  function normalizeSearch(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function formatMoneyNoCode(amountMajor) {
    var amountNum = Number(amountMajor || 0);
    var cents = Math.round(amountNum * 100);
    var currency = window.Shopify?.currency?.active || 'USD';
    var value = cents / 100;

    try {
      var locale = currency === 'CAD' ? 'en-CA' : (document.documentElement?.lang || undefined);
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol'
      }).format(value);
    } catch (e) {
      /* noop */
    }

    if (window.Shopify?.formatMoney) {
      var formatted = window.Shopify.formatMoney(cents);
      var normalized = formatted
        .replace(/\u00a0/g, ' ')
        .replace(new RegExp('^' + currency + '\\s*\\$', 'i'), '$')
        .replace(/^[A-Z]{2}\s*\$/i, '$')
        .replace(/\s+[A-Z]{3}$/i, '')
        .trim();
      if (/[^\d\s.,-]/.test(normalized)) return normalized;
    }

    return value.toLocaleString(undefined, {
      style: 'currency',
      currency: currency
    });
  }

  function parseCardArgs(card) {
    var script = card.querySelector('script.regios-dopp-card-args[type="application/json"]');
    if (!script?.textContent?.trim()) return null;

    var base;
    try {
      base = JSON.parse(script.textContent.trim());
    } catch (e) {
      return null;
    }

    var variantId = parseInt(card.dataset.variantId || '', 10);
    var regularPriceInCents = parseInt(card.dataset.regularPriceCents || '', 10);
    if (
      !Number.isInteger(variantId) ||
      variantId <= 0 ||
      !Number.isInteger(regularPriceInCents) ||
      regularPriceInCents < 0
    ) {
      return null;
    }

    return Object.assign({}, base, {
      variantId: variantId,
      regularPriceInCents: regularPriceInCents,
      compareAtPriceInCents: null,
      quantity: 1
    });
  }

  function renderPrice(card, result) {
    var sale = Number(result?.salePrice?.amount);
    var regular = Number(result?.regularPrice?.amount);
    if (!Number.isFinite(sale) || !Number.isFinite(regular) || sale < 0 || regular <= 0) return;

    var priceRoot = card.querySelector('[data-regios-price]');
    var currentEl = priceRoot?.querySelector('.price__current');
    var wasEl = priceRoot?.querySelector('.price__was');
    if (!priceRoot || !currentEl || !wasEl) return;

    var discounted = sale < regular;
    currentEl.innerHTML = formatMoneyNoCode(discounted ? sale : regular);
    wasEl.innerHTML = discounted ? formatMoneyNoCode(regular) : '';
    wasEl.hidden = !discounted;
    wasEl.classList.toggle('visually-hidden', !discounted);
    wasEl.setAttribute('aria-hidden', String(!discounted));
    priceRoot.classList.toggle('price--on-sale', discounted);
    priceRoot.hidden = false;

    var percent = discounted ? Math.round((1 - sale / regular) * 100) : 0;
    renderBadge(card, percent);
    return Math.max(0, Math.round(toNumber(percent)));
  }

  function renderBadge(card, percent) {
    var badge = card.querySelector('[data-regios-discount-badge]');
    if (!badge) return;

    var safePercent = Math.max(0, Math.round(toNumber(percent)));
    if (!safePercent) {
      badge.hidden = true;
      return;
    }

    var template = badge.dataset.badgeTemplate || '[percent]% OFF';
    var text = template.replace(/\[percent\]/gi, String(safePercent));
    if (/\[[^\]]+\]/.test(text) && badge.dataset.badgeFallback) {
      text = badge.dataset.badgeFallback.replace(/\[percent\]/gi, String(safePercent));
    }
    badge.textContent = text;
    badge.hidden = false;
  }

  class DiscountVariantCards extends HTMLElement {
    connectedCallback() {
      this.filters = {
        material: new Set(),
        color: new Set(),
        discount: new Set(),
        keyword: new Set()
      };
      this.searchTerms = [];
      this.cards = Array.from(this.querySelectorAll('[data-discount-variant-card]'));
      this.filterButtons = Array.from(this.querySelectorAll('[data-discount-filter-button]'));
      this.searchInputEl = this.querySelector('[data-discount-search]');
      this.discountFilterRow = this.querySelector('[data-discount-dynamic-filter]');
      this.discountFilterOptions = this.querySelector('[data-discount-dynamic-options]');
      this.clearFiltersBtn = this.querySelector('[data-discount-clear-filters]');
      this.visibleCountEl = this.querySelector('[data-discount-visible-count]');
      this.noResultsEl = this.querySelector('[data-discount-no-results]');
      this.requiredColor = this.dataset.requiredColor === 'true';
      if (this.requiredColor) {
        const initialColor = this.filterButtons.find(button => button.dataset.filterType === 'color');
        if (initialColor) this.selectRequiredColor(initialColor.dataset.filterValue);
      }

      this.hydrateCards();

      this._onClick = this.handleClick.bind(this);
      this._onSearchInput = this.handleSearchInput.bind(this);
      this._onRegiosInitialized = this.syncRegiosPrices.bind(this);
      this._onCampaignPrice = () => {
        clearTimeout(this._campaignFilterTimer);
        this._campaignFilterTimer = setTimeout(() => {
          this.updateDiscountFilters();
          this.applyFilters();
        }, 80);
      };
      this.addEventListener('campaign:price-updated', this._onCampaignPrice);

      this.addEventListener('click', this._onClick);
      if (this.searchInputEl) this.searchInputEl.addEventListener('input', this._onSearchInput);
      window.addEventListener('regios-dopp:api-initialized', this._onRegiosInitialized, { once: true });

      this.applyFilters();
      this.syncRegiosPrices();
      requestAnimationFrame(() => this.syncRegiosPrices());
    }

    disconnectedCallback() {
      clearTimeout(this._campaignFilterTimer);
      this.removeEventListener('campaign:price-updated', this._onCampaignPrice);
      this.removeEventListener('click', this._onClick);
      if (this.searchInputEl) this.searchInputEl.removeEventListener('input', this._onSearchInput);
      window.removeEventListener('regios-dopp:api-initialized', this._onRegiosInitialized);
    }

    hydrateCards() {
      this.cards = Array.from(this.querySelectorAll('[data-discount-variant-card]'));
      this.cards.forEach((card) => {
        card.discountSearchText = normalizeSearch(card.dataset.search);
        card.discountSearchCompact = card.discountSearchText.replace(/\s+/g, '');
      });
    }

    handleClick(event) {
      const stepper = event.target.closest('[data-quantity-step]');
      if (stepper && this.contains(stepper)) {
        const input = stepper.closest('[data-campaign-quantity]').querySelector('input');
        if (!input.value || !Number.isFinite(Number(input.value))) input.value = input.min || '1';
        if (Number(stepper.dataset.quantityStep) > 0) input.stepUp();
        else input.stepDown();
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      var filterButton = event.target.closest('[data-discount-filter-button]');
      if (filterButton && this.contains(filterButton)) {
        event.preventDefault();
        this.toggleFilter(filterButton);
        return;
      }

      if (event.target.closest('[data-discount-clear-filters]')) {
        event.preventDefault();
        this.clearFilters();
      }
    }

    handleSearchInput() {
      this.searchTerms = tokens(normalizeSearch(this.searchInputEl ? this.searchInputEl.value : ''));
      this.applyFilters();
    }

    toggleFilter(button) {
      var type = button.dataset.filterType;
      var value = button.dataset.filterValue;
      if (!this.filters[type] || !value) return;

      if (type === 'color' && this.requiredColor) {
        this.selectRequiredColor(value);
        this.applyFilters();
        return;
      }

      if (type !== 'color') {
        var wasActive = this.filters[type].has(value);
        this.filters[type].clear();
        if (!wasActive) this.filters[type].add(value);
        this.filterButtons.forEach((filterButton) => {
          if (filterButton.dataset.filterType !== type) return;
          var isActive = !wasActive && filterButton === button;
          filterButton.classList.toggle('is-active', isActive);
          filterButton.setAttribute('aria-pressed', String(isActive));
        });
      } else if (this.filters[type].has(value)) {
        this.filters[type].delete(value);
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      } else {
        this.filters[type].add(value);
        button.classList.add('is-active');
        button.setAttribute('aria-pressed', 'true');
      }

      this.applyFilters();
    }

    selectRequiredColor(value) {
      this.filters.color.clear();
      this.filters.color.add(value);
      this.filterButtons.forEach(button => {
        if (button.dataset.filterType !== 'color') return;
        const active = button.dataset.filterValue === value;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    clearFilters() {
      const selectedColor = this.requiredColor ? Array.from(this.filters.color)[0] : null;
      Object.keys(this.filters).forEach((key) => this.filters[key].clear());
      this.searchTerms = [];
      if (this.searchInputEl) this.searchInputEl.value = '';
      this.filterButtons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });
      if (selectedColor) this.selectRequiredColor(selectedColor);
      this.applyFilters();
    }

    applyFilters() {
      var visibleCount = 0;

      this.cards.forEach((card) => {
        var visible = this.cardMatchesFilters(card);
        card.hidden = !visible;
        if (visible) visibleCount += 1;
      });
      if (this.requiredColor) {
        const selectedColor = Array.from(this.filters.color)[0];
        this.querySelectorAll('[data-discount-color-group]').forEach(group => {
          group.hidden = group.dataset.discountColorGroup !== selectedColor;
        });
      }

      if (this.visibleCountEl) this.visibleCountEl.textContent = String(visibleCount);
      if (this.noResultsEl) this.noResultsEl.hidden = visibleCount > 0;
      if (this.clearFiltersBtn) {
        var hasActiveFilters = Object.keys(this.filters).some((key) => (key !== 'color' || !this.requiredColor) && this.filters[key].size);
        this.clearFiltersBtn.hidden = !hasActiveFilters && this.searchTerms.length === 0;
      }
    }

    cardMatchesFilters(card) {
      return this.matchesSearch(card) &&
        this.matchesTokenSet(this.filters.material, card.dataset.materials) &&
        this.matchesTokenSet(this.filters.color, card.dataset.color) &&
        this.matchesTokenSet(this.filters.discount, card.dataset.discountPercent) &&
        this.matchesKeywordFilters(card);
    }

    matchesSearch(card) {
      if (!this.searchTerms.length) return true;
      var searchText = card.discountSearchText || '';
      var compactText = card.discountSearchCompact || '';
      return this.searchTerms.every((term) => searchText.includes(term) || compactText.includes(term));
    }

    matchesKeywordFilters(card) {
      if (!this.filters.keyword.size) return true;
      return Array.from(this.filters.keyword).every((keyword) => {
        var keywordTerms = tokens(normalizeSearch(keyword));
        return keywordTerms.length > 0 && keywordTerms.every((term) => {
          return card.discountSearchText.includes(term) || card.discountSearchCompact.includes(term);
        });
      });
    }

    matchesTokenSet(activeSet, cardValue) {
      if (!activeSet || activeSet.size === 0) return true;
      var cardTokens = tokens(cardValue);
      return Array.from(activeSet).every((token) => cardTokens.includes(token));
    }

    updateDiscountFilters() {
      if (!this.discountFilterRow || !this.discountFilterOptions) return;

      var percentValues = new Set();
      this.cards.forEach((card) => {
        var percent = parseInt(card.dataset.discountPercent || '', 10);
        if (Number.isInteger(percent) && percent > 0) percentValues.add(percent);
      });

      var sortedPercents = Array.from(percentValues).sort((a, b) => b - a);
      this.discountFilterRow.hidden = sortedPercents.length === 0;

      Array.from(this.filters.discount).forEach((percent) => {
        if (!percentValues.has(parseInt(percent, 10))) this.filters.discount.delete(percent);
      });

      this.discountFilterOptions.innerHTML = sortedPercents.map((percent) => {
        var value = String(percent);
        var active = this.filters.discount.has(value);
        return '<button class="discount-variant-cards__filter-chip' + (active ? ' is-active' : '') + '" type="button" data-discount-filter-button data-filter-type="discount" data-filter-value="' + value + '" aria-pressed="' + String(active) + '">' + value + '% OFF</button>';
      }).join('');

      this.filterButtons = Array.from(this.querySelectorAll('[data-discount-filter-button]'));
    }

    syncRegiosPrices() {
      if (this.dataset.campaignPricing === 'true') {
        this.updateDiscountFilters();
        return;
      }
      var api = window.RegiosDOPP?.api?.v0;
      if (!api) return;

      this.cards.forEach((card) => {
        var args = parseCardArgs(card);
        if (!args) return;

        var interop = api.themeInterop;
        if (interop?.updateOnPagePrices) {
          interop.updateOnPagePrices(card, card.dataset.regiosDoppExtensionType || 'COLLECTION_PAGE', args).catch(() => {});
        }

        if (api.calculateDiscountedPrices) {
          api.calculateDiscountedPrices(args)
            .then((result) => {
              var percent = renderPrice(card, result);
              if (Number.isInteger(percent) && percent > 0) {
                card.dataset.discountPercent = String(percent);
              } else {
                delete card.dataset.discountPercent;
              }
              this.updateDiscountFilters();
              this.applyFilters();
            })
            .catch(() => {});
        }
      });
    }
  }

  if (!customElements.get('discount-variant-cards')) {
    customElements.define('discount-variant-cards', DiscountVariantCards);
  }
})();
